import { FC, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Checkbox,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ConfirmationDialog, Loader } from '@netiva/classifieds-ui';

import { PageTitle } from '@/components/global';
import { PriceSummary, PrintPriceSummary } from '@/components/ui';
import { routes } from '@/lib/routes';

import { AdNotice, Layout, NavigationBar, Preview, Step } from './components';
import { CommonStepKeys } from './constants';
import { useAd, useAdPrice, useAdPublishing } from './hooks';
import { AdProvider } from './providers';
import { getFilterStatus } from '../MyAds/utils';
import { useAppSelector } from '@/store';
import { useMatomoTagManager } from '@/hooks';

const AdPageInner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    steps,
    validationStatus,
    gotoNextStep,
    gotoPrevStep,
    gotoStep,
    currentStep,
    currentStepIndex,
    prevStep,
    isNew,
    save,
    saveAndPublish,
    isLoading,
    isSubmitting,
    isPrintAdvert,
    hasChanges,
    canSave,
    dataObject,
    price,
  } = useAd();

  const {
    selectedProductId,
    selectedExtensionProductIds,
    paymentProvider,
    platform,
    print,
    propertyFormData,
    categoryIsSuggested,
    images,
    documents,
    selectedProductTrackingInfo,
    selectedExtensionProductTrackingInfo,
  } = useAppSelector((state) => state.ad);

  const { trackAdStep, trackMatomoEvent } = useMatomoTagManager();

  const canGoBack =
    currentStep?.prevNavigationType === 'Enabled' || (isNew && currentStep?.prevNavigationType === 'Unsaved');

  const { requiresPublication } = useAdPublishing();

  const [tosChecked, setTosChecked] = useState(false);
  const toast = useToast();
  const closeConfirmation = useDisclosure();

  const isPaymentSelected = print.paymentInfo?.paymentType !== 'ImmediateRequired' || Boolean(paymentProvider);
  const allowSubmit = tosChecked && isPaymentSelected;

  const handleSubmit = () => {
    if (!tosChecked) {
      toast({
        status: 'error',
        description: t('ad.steps.review.submitTosError'),
        isClosable: true,
      });
      return;
    }
    trackStepAction();
    saveAndPublish();
  };

  const handleClose = () => {
    if (hasChanges) {
      closeConfirmation.onOpen();
    } else {
      trackMatomoEvent({
        trackingCategory: 'Interaction_PI-Insertion',
        trackingAction: 'Close_Insertionflow',
        trackingName: `Close_Insertionflow_${currentStep?.key}`,
      });
      navigate(routes.myAds({ status: getFilterStatus(dataObject?.status) }));
    }
  };
  const handleCloseCancel = () => {
    closeConfirmation.onClose();
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-Insertion',
      trackingAction: 'Close_Insertionflow',
      trackingName: `Close_Insertionflow_${currentStep?.key}`,
    });
    navigate(routes.myAds({ status: getFilterStatus(dataObject?.status) }));
  };
  const handleCloseConfirm = () => {
    if (isNew) {
      trackMatomoEvent({
        trackingCategory: 'Interaction_PI-Insertion',
        trackingAction: 'Save_Draft',
        trackingName: `Save_Draft_${currentStep?.key}`,
      });
    } else {
      trackMatomoEvent({
        trackingCategory: 'Interaction_PI-Insertion',
        trackingAction: `Save_Edited`,
        trackingName: `Save_Editited_${currentStep?.key}`,
      });
    }
    save().then(closeConfirmation.onClose);
  };

  const handleSave = () => {
    handleCloseConfirm();
  };

  const handleNextClick = () => {
    trackStepAction();
    gotoNextStep();
  };

  const handleBackClick = () => {
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-Insertion',
      trackingAction: 'Go_Back',
      trackingName: `Go_Back_${prevStep?.key}`,
    });
    gotoPrevStep();
  };

  const { isFree, priceBillingAddressId } = useAdPrice();

  // matomo tracking
  const isMatomoTrackedRef = useRef(false);

  useEffect(() => {
    if (!isMatomoTrackedRef.current && isNew) {
      isMatomoTrackedRef.current = true;
      trackMatomoEvent({
        trackingCategory: 'Interaction_PI-Insertion',
        trackingAction: `Start_Listing`,
      });
    }
  }, [isNew, trackMatomoEvent]);

  const trackStepAction = () => {
    trackAdStep({
      stepIndex: currentStepIndex,
      stepKey: currentStep?.key,
      propertyFormData,
      platform,
      categoryIsSuggested,
      images,
      documents,
      selectedProductTrackingInfo,
      selectedExtensionProductTrackingInfo,
      paymentProvider,
    });
  };

  return (
    <Layout onClose={handleClose}>
      {!!currentStep && !currentStep.isHidden && (
        <NavigationBar
          steps={steps}
          currentStepIndex={currentStepIndex}
          gotoStep={gotoStep}
          validationStatus={validationStatus}
        />
      )}
      <Box bgColor="white" padding={[4, 6, 8]}>
        <PageTitle titleRes={isNew ? 'ad.create.title' : 'ad.edit.title'} />
        <Loader isLoading={!currentStep || isLoading} showOverlay />
        {!!currentStep && (
          <>
            <Heading as="h1" size="md" mb={6}>
              {t(`${currentStep.titleResourceKey}.${platform?.toLowerCase()}`, '') ||
                t(currentStep.titleResourceKey, '')}
            </Heading>
            {/* Below lg the platform cards are stacked, so PlatformSelector shows the notice next to the selected card. */}
            {currentStep.key === CommonStepKeys.platform && <AdNotice display={{ base: 'none', lg: 'flex' }} mb={6} />}
            {currentStep.introResourceKey && (
              <Text
                as="div"
                sx={{
                  mb: 6,
                  'ul, ol': {
                    mb: 3,
                    pl: 5,
                  },
                  'ul li': {
                    listStyle: 'disc',
                    pl: 1,
                  },
                  'ol li': {
                    listStyle: 'list-item',
                    pl: 1,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    t(`${currentStep.introResourceKey}.${platform?.toLowerCase()}`, '') ||
                    t(currentStep.introResourceKey, ''),
                }}
              />
            )}
            <Grid
              templateColumns={{
                base: '1fr',
                md: currentStep.showPreview || currentStep.showPriceSummary ? '2fr minmax(min-content, 1.1fr)' : '1fr',
              }}
              gap={8}
            >
              <GridItem>
                <Step step={currentStep} />
                {currentStep.nextNavigationType === 'Publish' && requiresPublication && (
                  <Checkbox isChecked={tosChecked} onChange={(e) => setTosChecked(e.target.checked)} mt={4} isRequired>
                    <span dangerouslySetInnerHTML={{ __html: t('ad.steps.review.tosText') }}></span>
                  </Checkbox>
                )}
              </GridItem>
              {(currentStep.showPreview || currentStep.showPriceSummary) && (
                <GridItem>
                  <Divider display={{ md: 'none' }} mb={4} />
                  {isPrintAdvert && <Preview />}
                  {currentStep.showPriceSummary && (
                    <>
                      {isPrintAdvert ? (
                        <PrintPriceSummary
                          price={price}
                          issueCount={print.selectedIssueIds.length}
                          showMultipleIssues={currentStep.showPriceSummaryTotal}
                        />
                      ) : (
                        <PriceSummary
                          productId={selectedProductId}
                          extensionProductIds={selectedExtensionProductIds}
                          billingAddressId={priceBillingAddressId}
                          position="sticky"
                          top={4}
                        />
                      )}
                    </>
                  )}
                </GridItem>
              )}
            </Grid>
            <Divider my={4} />
            <Flex justify="flex-end" align="center" gap={4} wrap="wrap">
              {canGoBack && (
                <Button colorScheme="neutral" onClick={handleBackClick} mr="auto">
                  {t('ui.buttons.previous')}
                </Button>
              )}
              {canSave && (
                <Button colorScheme="neutral" onClick={handleSave} disabled={isLoading} isLoading={isSubmitting}>
                  {isNew ? t('ad.saveAsDraft') : t('ad.saveAndClose')}
                </Button>
              )}
              {currentStep.nextNavigationType === 'Enabled' && (
                <Button
                  onClick={handleNextClick}
                  isLoading={isLoading}
                  disabled={isLoading}
                  data-cpt-event={`Confirm_Step_${platform}_${currentStep.key}`}
                >
                  {t('ui.buttons.next')}
                </Button>
              )}
              {currentStep.nextNavigationType === 'Publish' && requiresPublication && (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  colorScheme={allowSubmit ? undefined : 'neutral'}
                  data-cpt-event={`Confirm_Step_${platform}_${currentStep.key}`}
                >
                  {t(isFree ? 'ad.steps.review.submitFree' : 'ad.steps.review.submit')}
                </Button>
              )}
            </Flex>
          </>
        )}
        <ConfirmationDialog
          isOpen={closeConfirmation.isOpen}
          onCancel={handleCloseCancel}
          onConfirm={handleCloseConfirm}
          title={t('ad.closeConfirmation.title')}
          message={t('ad.closeConfirmation.message')}
          cancelText={t('ui.buttons.no')}
          okText={t('ui.buttons.yes')}
        />
      </Box>
    </Layout>
  );
};

export const AdPage: FC = memo(function AdPage() {
  return (
    <AdProvider>
      <AdPageInner />
    </AdProvider>
  );
});
